import { useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import BannerAlert, { BannerProps } from "@/components/wpl/banner-alert/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { GlobalFooter } from "@/patterns/global-footer/src/index";
import StickyContainer from "@/patterns/sticky-container/sticky-container";
import { experiments } from "@/utils/experiment";
import { ExperimentView } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import JsmUseCaseProductTourOnWac from "./jsm-use-case/sections/hero";
import GWP6480JSMEmbeddedSignup from "./jsm-use-case/sections/hero-embedded-signup";
import HeroTabs from "./jsm-use-case/sections/hero-tabs";
import { AtlassianIntelligenceITSM } from "./sections/ai-itsm";
import CardsSection from "./sections/cards";
import CTASection from "./sections/cta";
import CustomersSection from "./sections/customers";
import IntegrationSection from "./sections/integration";
import ScalingSection from "./sections/scaling";
import JiraServiceManagementSoftwareNavV2 from "./sections/software-jira-service-management-navbarv2";
import TabsSection from "./sections/tabs";
import TilesSection from "./sections/tiles";

export const LandingBannerAlert = () => {
    const intl = useIntl();

    const showForrester = checkFeatureFlagGate(
        featureFlags.gwp6701ForresterBanner
    );

    const showAiBanner = checkFeatureFlagGate(
        featureFlags.wacUipButterBarJsmGate
    );

    const showHighVelocityBanner = checkFeatureFlagGate(
        featureFlags.gwp7942JsmWebinarGate
    );

    const forresterBannerConfig: BannerProps = {
        identifier: "jira-service-management-software.banner.forrester-devops",
        text: "Atlassian recognized as a Leader in The Forrester Wave™: DevOps Platforms, Q2 2025.",
        linkText: "Learn more",
        linkUrl: useLocalizedHref(
            "/forrester/forrester-wave-devops?utm_source=in-product-banner&utm_medium=banner&utm_campaign=P:bitbucket*O:clm*I:banner-wac-jiraservicemanagement*Y:cloud_mig*E:dc-cloud*&utm_sfdc-campaign_id=701QB00000SlywgYAB"
        ),
        dataEventProps: {
            "data-product-key": "jiraServiceManagement",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked"
        }
    };

    const aiBannerMessages: BannerProps = {
        identifier:
            "jira-service-management-software.atlassian-intelligence_exp",
        text: intl.formatMessage({
            id: "jira-service-management-software.banner-alert.atlassian-intelligence_exp",
            defaultMessage:
                "Transform teamwork with Atlassian Intelligence - now available."
        }),
        linkText: intl.formatMessage({
            id: "jira-service-management-software.link-text",
            defaultMessage: "Learn more"
        }),
        linkUrl: useLocalizedHref("/software/artificial-intelligence"),
        dataEventProps: {
            "data-product-key": "jiraServiceManagement",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Learn more",
            "data-label-english": "Learn more"
        }
    };

    const highVelocityBannerConfig: BannerProps = {
        identifier: "jira-service-management-software.banner.high-velocity",
        text: intl.formatMessage({
            id: "jira-service-management-software.banner-alert.high-velocity",
            defaultMessage:
                "Meet us at Atlassian Connect: High Velocity for networking, panels, workshops, and demos around the latest AI-powered innovations for high-velocity service management."
        }),
        linkText: intl.formatMessage({
            id: "jira-service-management-software.banner-alert.high-velocity.link-text",
            defaultMessage: "Register for free"
        }),
        linkUrl:
            "https://events.atlassian.com/connect-highvelocity?utm_medium=event-web-referral&utm_source=cross-promo&event_context=jsm_product_page&utm_campaign=K:event|H:in-person|N:connect_hv|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=wac_banner&event_id=284907",
        dataEventProps: {
            "data-product-key": "jiraServiceManagement",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Register for free",
            "data-label-english": "Register for free"
        }
    };
    if (showHighVelocityBanner) {
        return <BannerAlert {...highVelocityBannerConfig} />;
    }
    if (showForrester) {
        return <BannerAlert {...forresterBannerConfig} />;
    }
    if (showAiBanner) {
        return <BannerAlert {...aiBannerMessages} />;
    }

    return null;
};

export const JiraServiceManagementSoftwarePageTemplate = () => {
    return (
        <div>
            <StickyContainer>
                <JiraServiceManagementSoftwareNavV2 />
            </StickyContainer>
            <UFOSegment name="jsm-landing-banner">
                <LandingBannerAlert />
            </UFOSegment>
            <main id="content">
                <ExperimentView
                    experiment={experiments.gwp6480JSMEmbeddedSignup}
                    render={(isVariant) => {
                        if (isVariant === true) {
                            return (
                                <UFOSegment name="jsm-hero-embedded-signup">
                                    <GWP6480JSMEmbeddedSignup />
                                </UFOSegment>
                            );
                        }
                        return (
                            <UFOSegment name="jsm-landing-hero">
                                <JsmUseCaseProductTourOnWac />
                            </UFOSegment>
                        );
                    }}
                />
                <UFOSegment name="jsm-landing-hero-tabs">
                    <HeroTabs />
                </UFOSegment>
                <UFOSegment name="jsm-landing-aiItsmSection">
                    <AtlassianIntelligenceITSM />
                </UFOSegment>
                <UFOSegment name="jsm-landing-tabsSection">
                    <TabsSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-customersSection">
                    <CustomersSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-scalingSection">
                    <ScalingSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-tilesSection">
                    <TilesSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-cardsSection">
                    <CardsSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-integrationSection">
                    <IntegrationSection />
                </UFOSegment>
                <UFOSegment name="jsm-landing-ctaSection">
                    <CTASection />
                </UFOSegment>
            </main>
            <UFOSegment name="jsm-landing-globalFooter">
                <GlobalFooter />
            </UFOSegment>
        </div>
    );
};
